import { useState, useEffect, useRef } from 'react'
import { FloppyDisk, MapPin, CheckCircle, XCircle } from '@phosphor-icons/react'
import { supabase } from '../lib/supabase'

async function saveDataToSupabase(projectId, data) {
  try {
    const { data: { user }, error: authErr } = await supabase.auth.getUser()
    if (authErr || !user) return
    const { error } = await supabase.from('evaluations').upsert({
      project_id: projectId,
      user_id: user.id,
      category: 'Transporte',
      criteria_code: 'TRA2-DATA',
      criteria_name: 'TRA2 Datos',
      points_obtained: 0, points_max: 0, cumple: false,
      justificacion: JSON.stringify(data),
      updated_at: new Date().toISOString(),
    }, { onConflict: 'project_id,category,criteria_code' })
    if (error) console.error('saveData error:', error.message)
  } catch (e) { console.error('saveData exception:', e.message) }
}

async function loadDataFromSupabase(projectId) {
  try {
    const { data } = await supabase.from('evaluations')
      .select('justificacion').eq('project_id', projectId)
      .eq('criteria_code', 'TRA2-DATA').single()
    if (data?.justificacion) return JSON.parse(data.justificacion)
  } catch (e) {}
  return null
}

// ── Estructura de servicios según Tabla 27 (BREEAM ES NC 2015 TRA 2)
const SERVICIOS_COL1 = [
  { key: 'comercial',  label: 'Comercial',  hint: 'Supermercado, farmacia, banco/cajero, tienda…' },
  { key: 'servicios',  label: 'Servicios',  hint: 'Correos, lavandería, proveedor de servicios…' },
]

const SERVICIOS_COL2 = [
  { key: 'hostelero',    label: 'Hostelero',                       hint: 'Restaurante, cafetería, bar…' },
  { key: 'docente',      label: 'Docente',                         hint: 'Colegio, escuela, guardería, universidad…' },
  { key: 'sanitario',    label: 'Sanitario / Asistencial',         hint: 'Centro médico, hospital, farmacia (si no cuenta como comercial)…' },
  { key: 'deportivo',    label: 'Centro deportivo / Ocio',         hint: 'Gimnasio, piscina, polideportivo, cine…' },
  { key: 'zonaExterior', label: 'Zona exterior de libre acceso',   hint: 'Parque público, jardín, plaza…' },
  { key: 'cultural',     label: 'Cultural',                        hint: 'Biblioteca, museo, teatro, centro cultural…' },
]

const BUILDING_TYPES = ['Tipo 1 y 2', 'Tipo 3', 'Tipo 7', 'Tipo 4 y 5']

const INP_STYLE = {
  padding: '0.3rem 0.45rem', fontSize: '0.72rem',
  border: '1px solid #C8D8C0', borderRadius: '4px',
  background: 'white', fontFamily: "'IBM Plex Mono', monospace",
  boxSizing: 'border-box', color: '#2C2017',
}

function ServiceRow({ sKey, label, hint, maxDist, servicio, onChange }) {
  const d = parseFloat(servicio.dist)
  const withinLimit = servicio.present && d <= maxDist
  const overLimit   = servicio.present && d > maxDist
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.45rem 0.5rem', borderRadius: '4px', background: withinLimit ? '#EAF3EC' : overLimit ? '#FDF5F3' : 'transparent', marginBottom: '0.3rem' }}>
      <input
        type="checkbox"
        checked={servicio.present}
        onChange={e => onChange(sKey, 'present', e.target.checked)}
        style={{ width: '14px', height: '14px', cursor: 'pointer', accentColor: '#2E4A38', flexShrink: 0 }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '0.72rem', fontWeight: '600', color: '#2C2017' }}>{label}</div>
        <div style={{ fontSize: '0.58rem', color: '#9A8A7A' }}>{hint}</div>
      </div>
      <input
        type="number" min="0" step="1"
        value={servicio.dist}
        placeholder={`≤${maxDist}m`}
        disabled={!servicio.present}
        onChange={e => onChange(sKey, 'dist', e.target.value)}
        style={{ ...INP_STYLE, width: '90px', opacity: servicio.present ? 1 : 0.4, borderColor: overLimit ? '#D4614A' : withinLimit ? '#6A9A7A' : '#C8D8C0' }}
      />
      <div style={{ width: '16px', flexShrink: 0 }}>
        {withinLimit && <CheckCircle size={16} weight="fill" style={{ color: '#3B7A50' }} />}
        {overLimit   && <XCircle    size={16} weight="fill" style={{ color: '#D4614A' }} />}
      </div>
    </div>
  )
}

function initServicios() {
  const s = {}
  ;[...SERVICIOS_COL1, ...SERVICIOS_COL2].forEach(({ key }) => {
    s[key] = { present: false, dist: '' }
  })
  return s
}

function calcPts(tipoEdificio, servicios) {
  // Columna 1: Comercial + Servicios ≤500m — prerequisito para cualquier punto
  const col1OK = SERVICIOS_COL1.every(({ key }) => {
    const s = servicios[key]
    return s.present && parseFloat(s.dist) <= 500
  })

  if (!col1OK) return 0

  // Tipos 4 y 5: máximo 1 punto, solo columna 1
  if (tipoEdificio === 'Tipo 4 y 5') return 1

  // Tipos 1, 2, 3 y 7: columna 2 suma el 2º punto
  // Se necesitan ≥2 de los 6 servicios especiales ≤1000m
  const col2Count = SERVICIOS_COL2.filter(({ key }) => {
    const s = servicios[key]
    return s.present && parseFloat(s.dist) <= 1000
  }).length

  return col2Count >= 2 ? 2 : 1
}

export default function TRA2Calculator({ projectId, onSave, saving }) {
  const storageKey = `tra2_${projectId}`
  const [savedOk, setSavedOk] = useState(false)

  const [tipoEdificio, setTipoEdificio] = useState(() => {
    if (!projectId) return 'Tipo 3'
    try { return JSON.parse(localStorage.getItem(`tra2_${projectId}`) || 'null')?.tipoEdificio || 'Tipo 3' } catch { return 'Tipo 3' }
  })
  const [servicios, setServicios] = useState(() => {
    if (!projectId) return initServicios()
    try {
      const saved = JSON.parse(localStorage.getItem(`tra2_${projectId}`) || 'null')
      return saved?.servicios || initServicios()
    } catch { return initServicios() }
  })

  // Ref-guard: bloquea el persist hasta que el load de Supabase termine (BUG-race).
  const hasLoaded = useRef(false)

  // Cargar desde Supabase al montar
  useEffect(() => {
    if (!projectId) return
    loadDataFromSupabase(projectId).then(remote => {
      if (remote) {
        if (remote.tipoEdificio) setTipoEdificio(remote.tipoEdificio)
        if (remote.servicios) setServicios(remote.servicios)
      }
      hasLoaded.current = true
    })
  }, [projectId]) // eslint-disable-line

  // Persistir: localStorage inmediato + Supabase debounced 1.5s (BUG-009)
  const supabaseSaveTimer = useRef(null)
  useEffect(() => {
    if (!projectId) return
    if (!hasLoaded.current) return
    const data = { tipoEdificio, servicios }
    localStorage.setItem(storageKey, JSON.stringify(data))
    if (supabaseSaveTimer.current) clearTimeout(supabaseSaveTimer.current)
    supabaseSaveTimer.current = setTimeout(() => saveDataToSupabase(projectId, data), 1500)
    return () => { if (supabaseSaveTimer.current) clearTimeout(supabaseSaveTimer.current) }
  }, [tipoEdificio, servicios, projectId]) // eslint-disable-line

  // Flush al desmontar
  useEffect(() => {
    return () => {
      if (!projectId) return
      if (!hasLoaded.current) return
      const data = { tipoEdificio, servicios }
      localStorage.setItem(storageKey, JSON.stringify(data))
      if (supabaseSaveTimer.current) {
        clearTimeout(supabaseSaveTimer.current)
        saveDataToSupabase(projectId, data)
      }
    }
  }, [projectId, tipoEdificio, servicios]) // eslint-disable-line

  function saveNow() {
    const data = { tipoEdificio, servicios }
    localStorage.setItem(storageKey, JSON.stringify(data))
    saveDataToSupabase(projectId, data)
    setSavedOk(true)
    setTimeout(() => setSavedOk(false), 2000)
  }

  const updateServicio = (key, field, value) =>
    setServicios(prev => ({ ...prev, [key]: { ...prev[key], [field]: value } }))

  const isTipo45 = tipoEdificio === 'Tipo 4 y 5'

  // Cálculos en vivo
  const col1Status = SERVICIOS_COL1.map(({ key, label }) => {
    const s = servicios[key]
    const d = parseFloat(s.dist)
    const ok = s.present && d <= 500
    const overLimit = s.present && d > 500
    return { key, label, ok, overLimit, present: s.present }
  })
  const col1OK = col1Status.every(s => s.ok)

  const col2Status = SERVICIOS_COL2.map(({ key, label }) => {
    const s = servicios[key]
    const d = parseFloat(s.dist)
    const ok = s.present && d <= 1000
    const overLimit = s.present && d > 1000
    return { key, label, ok, overLimit, present: s.present }
  })
  const col2Count = col2Status.filter(s => s.ok).length
  const col2OK = col2Count >= 2

  const pts = calcPts(tipoEdificio, servicios)

  // ── Estilos
  const labelStyle = {
    fontSize: '0.58rem', fontWeight: '700', color: '#5A7A4A',
    textTransform: 'uppercase', letterSpacing: '0.06em',
    display: 'block', marginBottom: '0.25rem',
  }

  return (
    <div style={{ marginBottom: '1.25rem', border: '2px solid #6A9A7A', borderRadius: '6px', overflow: 'hidden' }}>

      {/* Cabecera */}
      <div style={{ background: '#2E4A38', padding: '0.6rem 1rem', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
        <MapPin size={15} weight="fill" style={{ color: '#A8D0B8', flexShrink: 0 }} />
        <span style={{ fontSize: '0.65rem', fontWeight: '700', color: '#A8D0B8', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
          Calculadora BREEAM ES TRA 2 — Proximidad a los Servicios
        </span>
      </div>

      <div style={{ padding: '0.875rem', background: '#F5FAF6' }}>

        {/* Tipo de edificio */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={labelStyle}>Tipo de edificio</label>
          <select
            value={tipoEdificio}
            onChange={e => setTipoEdificio(e.target.value)}
            style={{ ...INP_STYLE, width: 'auto', border: '1px solid #6A9A7A' }}
          >
            {BUILDING_TYPES.map(t => <option key={t}>{t}</option>)}
          </select>
        </div>

        {/* ─── Servicios ─── */}
        <div style={{ marginBottom: '1rem', border: `1px solid ${pts ? '#6A9A7A' : '#DDD5C8'}`, borderRadius: '6px', overflow: 'hidden' }}>
          <div style={{ background: pts ? '#2E4A38' : '#F0EBE3', padding: '0.5rem 0.875rem', display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '0.65rem', fontWeight: '700', color: pts ? '#A8D0B8' : '#7A6A5A', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Proximidad a servicios (Tabla 27)
            </span>
            <span style={{ fontSize: '0.65rem', fontWeight: '700', color: pts ? '#A8D0B8' : '#B0A89E', fontFamily: "'IBM Plex Mono', monospace" }}>
              {pts ? '✓ 1 pt' : '0 pt'}
            </span>
          </div>

          <div style={{ padding: '0.75rem' }}>

            {/* Columna 1 — prerequisito */}
            <div style={{ marginBottom: isTipo45 ? 0 : '0.875rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '0.62rem', fontWeight: '700', color: '#3B5A47', background: col1OK ? '#EAF3EC' : '#F0EBE3', border: `1px solid ${col1OK ? '#6A9A7A' : '#DDD5C8'}`, borderRadius: '4px', padding: '0.15rem 0.5rem' }}>
                  {isTipo45 ? '1 punto — Comercial + Servicios ≤ 500 m' : 'Columna 1 · 1 punto — Comercial + Servicios ≤ 500 m'}
                </span>
                <span style={{ fontSize: '0.58rem', color: col1OK ? '#3B7A50' : '#9A8A7A', fontWeight: col1OK ? '600' : '400' }}>
                  {col1OK ? '✓ cumple' : `Falta${col1Status.filter(s => !s.ok).length > 1 ? 'n' : ''}: ${col1Status.filter(s => !s.ok).map(s => s.label).join(', ')}`}
                </span>
              </div>
              {SERVICIOS_COL1.map(({ key, label, hint }) => (
                <ServiceRow key={key} sKey={key} label={label} hint={hint} maxDist={500} servicio={servicios[key]} onChange={updateServicio} />
              ))}
              <div style={{ fontSize: '0.58rem', color: '#9A8A7A', marginTop: '0.25rem' }}>
                * Solo cuenta un servicio de cada tipo (ej: farmacia + supermercado = 1 Comercial)
              </div>
            </div>

            {/* Columna 2 — solo Tipos 1-3, 7 */}
            {!isTipo45 && (
              <div style={{ opacity: col1OK ? 1 : 0.45 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '0.62rem', fontWeight: '700', color: '#3B5A47', background: col2OK ? '#EAF3EC' : '#F0EBE3', border: `1px solid ${col2OK ? '#6A9A7A' : '#DDD5C8'}`, borderRadius: '4px', padding: '0.15rem 0.5rem' }}>
                    Columna 2 · 2º punto — ≥ 2 de los siguientes ≤ 1 000 m
                  </span>
                  <span style={{ fontSize: '0.58rem', color: col2OK ? '#3B7A50' : '#9A8A7A', fontWeight: col2OK ? '700' : '400' }}>
                    {col2Count}/6 {col2OK ? '✓ cumple' : `(necesitas ${Math.max(0, 2 - col2Count)} más)`}
                  </span>
                </div>
                {SERVICIOS_COL2.map(({ key, label, hint }) => (
                  <ServiceRow key={key} sKey={key} label={label} hint={hint} maxDist={1000} servicio={servicios[key]} onChange={updateServicio} />
                ))}
                {!col1OK && (
                  <div style={{ fontSize: '0.6rem', color: '#C98A2E', marginTop: '0.35rem', background: '#FDF8EE', border: '1px solid #E8D898', borderRadius: '4px', padding: '0.35rem 0.6rem' }}>
                    ⚠ Debes cumplir primero la Columna 1 (Comercial + Servicios ≤500m) para poder sumar este punto.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ─── Resultados ─── */}
        <div style={{ background: 'white', border: '1px solid #B8D4C0', borderRadius: '6px', padding: '0.875rem' }}>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.875rem' }}>

            <div style={{ background: '#EAF3EC', border: '2px solid #2E4A38', borderRadius: '6px', padding: '0.5rem 1.25rem', textAlign: 'center', minWidth: '100px' }}>
              <div style={{ fontSize: '0.55rem', fontWeight: '700', color: '#2E4A38', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.2rem' }}>Puntos TRA 2</div>
              <div style={{ fontSize: '1.6rem', fontWeight: '800', color: '#1A3020', fontFamily: "'IBM Plex Mono', monospace", lineHeight: 1 }}>
                {pts}<span style={{ fontSize: '0.8rem', color: '#6A9A7A' }}>/{isTipo45 ? 1 : 2}</span>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {pts
                ? <CheckCircle size={16} weight="fill" style={{ color: '#3B7A50' }} />
                : <XCircle    size={16} weight="fill" style={{ color: '#B0A89E' }} />}
              <span style={{ fontSize: '0.7rem', color: pts ? '#2E4A38' : '#9A8A7A', fontWeight: pts ? '600' : '400' }}>
                {pts === 2 ? 'Columna 1 ✓ + Columna 2 ✓ → 2 pts'
                  : pts === 1 ? `Columna 1 ✓${!isTipo45 ? ' · Columna 2 pendiente' : ''} → 1 pt`
                  : `Columna 1 pendiente${!isTipo45 ? ` · col2: ${col2Count}/2` : ''} → 0 pts`}
              </span>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', alignItems: 'center' }}>
            <button onClick={saveNow} style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
              padding: '0.35rem 0.75rem', background: 'transparent',
              color: savedOk ? '#8D9A2E' : '#7A6A5A',
              border: `1px solid ${savedOk ? '#8D9A2E' : '#DDD5C8'}`,
              borderRadius: '5px', cursor: 'pointer', fontSize: '0.78rem',
              fontFamily: "'DM Sans', sans-serif"
            }}>
              <FloppyDisk size={14} />{savedOk ? '✓ Guardado' : 'Guardar datos'}
            </button>
            <button
              onClick={() => onSave({ pts, tipoEdificio, servicios })}
              disabled={saving}
              style={{ background: '#3B2F2F', color: '#F5F0EA', border: 'none', borderRadius: '6px', padding: '0.5rem 1rem', fontSize: '0.775rem', fontWeight: '600', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: '0.4rem', fontFamily: "'DM Sans', sans-serif" }}
            >
              <FloppyDisk size={14} />
              {saving ? 'Guardando…' : `Guardar puntuación TRA 2 (${pts} pt)`}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
